import { type FC, memo } from 'react';
import styled from 'styled-components';

import { TEXT_COLOR_BY_BACKGROUND_COLOR } from '@wecasa/common/src/components/label/styles';
import type { LabelCommonProps, LabelSize } from '@wecasa/common/src/components/label/types';
import colors from '@wecasa/common/src/utils/colors';

import type { TextProps } from '../base-text/base-text';
import Text from '../base-text/variants/text';
import Title from '../base-text/variants/title';
import Icon, { type IconProps } from '../icon/icon';
import type { WithClassNameProps } from '../types';

const ICON_SIZE_BY_SIZE: Record<LabelSize, number> = {
  xxs: 9,
  xs: 11,
  sm: 13,
  md: 15,
  lg: 18,
};

const Container = styled.div<{ $backgroundColor: string }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 4px;
  border-radius: 2px;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
`;

const TEXT_COMPONENT_BY_SIZE: Record<LabelSize, FC<TextProps>> = {
  xxs: Text.XXSBold,
  xs: Text.XSBold,
  sm: Text.SMBold,
  md: Text.MDBold,
  lg: Title.MD,
};

export type LabelProps = WithClassNameProps &
  Pick<TextProps, 'children'> &
  LabelCommonProps & {
    icon?: IconProps['name'];
  };

const Label = ({ backgroundColor = 'neutral100', size = 'md', children, className, icon }: LabelProps) => {
  const textColor = colors[TEXT_COLOR_BY_BACKGROUND_COLOR[backgroundColor]];
  const TextComponent = TEXT_COMPONENT_BY_SIZE[size];

  return (
    <Container $backgroundColor={colors[backgroundColor]} className={className}>
      {!!icon && <Icon name={icon} size={ICON_SIZE_BY_SIZE[size]} color={textColor} />}
      <TextComponent color={textColor}>{children}</TextComponent>
    </Container>
  );
};

export default memo(Label);
